/**
 * 密码强度指示器组件
 * 显示密码强度等级和详细标准满足情况
 */

import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { usePasswordStrength } from "@/hooks/usePasswordStrength";
import { Check, X } from "lucide-react";

export interface PasswordStrengthIndicatorProps {
  /** 密码值 */
  password: string;
  /** 是否显示详细标准 */
  showCriteria?: boolean;
  /** 自定义类名 */
  className?: string;
}

/**
 * 密码强度指示器组件
 */
export default function PasswordStrengthIndicator({
  password,
  showCriteria = false,
  className,
}: PasswordStrengthIndicatorProps) {
  const { t } = useTranslation("login");
  const { strength, score, criteria, color, labelKey } =
    usePasswordStrength(password);

  // 如果密码为空，不显示指示器
  if (!password) {
    return null;
  }

  // 颜色映射
  const colorClasses = {
    red: "bg-red-500",
    yellow: "bg-yellow-500",
    green: "bg-green-500",
  };

  const textColorClasses = {
    red: "text-red-600 dark:text-red-400",
    yellow: "text-yellow-600 dark:text-yellow-400",
    green: "text-green-600 dark:text-green-400",
  };

  return (
    <div className={cn("space-y-2", className)}>
      {/* 强度条 */}
      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="text-xs text-muted-foreground">
            {t("passwordStrength.label", "密码强度")}
          </span>
          <span className={cn("text-xs font-medium", textColorClasses[color])}>
            {t(labelKey, strength)}
          </span>
        </div>
        <div className="h-1.5 w-full bg-muted rounded-full overflow-hidden">
          <div
            className={cn(
              "h-full transition-all duration-300 rounded-full",
              colorClasses[color]
            )}
            style={{ width: `${score}%` }}
            role="progressbar"
            aria-valuenow={score}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={t(labelKey, strength)}
          />
        </div>
      </div>

      {/* 详细标准（可选） - 只显示字数要求 */}
      {showCriteria && (
        <ul className="space-y-1 text-xs">
          <CriteriaItem
            met={criteria.minLength}
            label={t("passwordStrength.minLength", "至少 6 个字符")}
          />
        </ul>
      )}
    </div>
  );
}

/**
 * 标准项组件
 */
function CriteriaItem({ met, label }: { met: boolean; label: string }) {
  return (
    <li className="flex items-center gap-1.5">
      {met ? (
        <Check className="h-3 w-3 text-green-500" aria-hidden="true" />
      ) : (
        <X className="h-3 w-3 text-muted-foreground" aria-hidden="true" />
      )}
      <span className={cn(met ? "text-foreground" : "text-muted-foreground")}>
        {label}
      </span>
    </li>
  );
}
